import { v4 as uuidv4 } from 'uuid';

/**
 * Base class for Velocity browser adapters that provides common functionality
 * for calling Velocity browser functions via wls: URL scheme.
 */
export abstract class VelocityBaseAdapter {
    /**
     * Converts non-ASCII characters in a ZPL payload to ZPL hex-encoded UTF-8.
     * This ensures the payload is pure ASCII, avoiding encoding issues when
     * transported through the wls: URL scheme and Velocity's session.js.
     *
     * The printer decodes hex bytes using ^FH (field hex indicator).
     * Font setup (^CI28, ^CWJ, ^AJN etc.) is expected in the upstream ZPL.
     *
     * Example: "^FD日本語^FS" → "^FH^FD_E6_97_A5_E6_9C_AC_E8_AA_9E^FS"
     */
    static encodeNonAsciiForZpl(zpl: string): string {
        if (![...zpl].some((c) => c.charCodeAt(0) > 127)) {
            return zpl;
        }
        const result = [...zpl]
            .map((char) => {
                if (char === '_') return '_5F';
                if (char.charCodeAt(0) > 127) return encodeURIComponent(char).replace(/%/g, '_');
                return char;
            })
            .join('');
        return result.replace(/\^FD/g, '^FH^FD');
    }

    /**
     * Calls a Velocity browser function with the given arguments.
     *
     * @param functionName - Name of the Velocity function to call
     * @param args - Arguments to pass to the function (will be converted to strings)
     * @returns Promise that resolves with the function result
     */
    public callVelocityFunction(functionName: string, ...args: any[]): Promise<any> {
        return this.callVelocityFunctionWithTimeout(this.createVelocityFunctionPromise(functionName, ...args));
    }

    /**
     * Creates a promise that calls a Velocity browser function asynchronously.
     *
     * @param functionName - Name of the Velocity function to call
     * @param args - Arguments to pass to the function (will be converted to strings)
     * @returns Promise that resolves when the Velocity function completes
     */
    private createVelocityFunctionPromise(functionName: string, ...args: any[]): Promise<any> {
        const uuid = uuidv4();
        const callbackName = `__velocity_callback_${uuid}`;

        /**
         * Ivanti velocity browser has logic built in when the url starts with the directive `wls:`
         * it doesn't perform a redirect and instead asynchronously runs a function passed in
         */
        const finalArgs = [...args.map((arg) => (typeof arg === 'number' ? arg : `'${String(arg)}'`)), `'${callbackName}'`].join(',');
        const functionUrl = `wls:${functionName}(${finalArgs})`;

        return new Promise((resolve) => {
            /**
             * Since the velocity function is asynchronous, we need to use a callback function to resolve this promise
             * when the logic has finished running. The velocity function checks for the existence of the callback
             * inside the window object, as it has no other existing context. We generate a uuid so that if we are
             * doing multiple print operations, they aren't using the same callback function.
             *
             * We need to use the top level window object instead of the normal window, as our tools are inside a nested
             * window object, but velocity browser is using a different window object, by using the `top` window, we
             * guarantee both the tool at the velocity browser are sharing resources.
             */
            (top as any)[callbackName] = function (result: any) {
                // @ts-ignore noImplicitThis
                console.log(`${this.constructor.name}: received response ${JSON.stringify(result)} from velocity function ${functionUrl}`);
                resolve(result);
                // After the callback function has run, delete the callback function as its no longer needed
                delete (top as any)[callbackName];
            }.bind(this);

            console.log(`${this.constructor.name} calling velocity browser function ${functionUrl}`);
            window.location.href = functionUrl;
        });
    }

    /**
     * Wraps a Velocity function call with a timeout to prevent hanging indefinitely.
     *
     * @param velocityCallPromise - Promise from the Velocity function call
     * @param timeoutMs - Timeout in milliseconds (default: 10000)
     * @returns Promise that resolves with the function result or rejects on timeout
     */
    private callVelocityFunctionWithTimeout(velocityCallPromise: Promise<any>, timeoutMs: number = 10000): Promise<any> {
        /**
         * Just in case velocity browser never finishes the promise asynchronously,
         * we shouldn't sit here waiting forever.
         */
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(this.createTimeoutError()), timeoutMs));

        return Promise.race([velocityCallPromise, timeoutPromise]);
    }

    /**
     * Creates an appropriate timeout error for the specific adapter.
     * Subclasses should override this to return their specific exception type.
     */
    protected abstract createTimeoutError(): Error;
}
